/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    useMultiStyleConfig,
    Box,
    Flex,
    IconButton,
    Badge,
    Link as ChakraLink,
} from '@chakra-ui/react'

// import Search from '@salesforce/retail-react-app/app/components/search'

import {useWishList} from '../../hooks/use-wish-list'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {FavouriteIcon, BurgerIcon, BrandLogo, SupportIcon, CloseIcon} from '../icons'
import MiniCartPopover from '../mini-cart-popover'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {useGetFooterByContentType} from '../../custom-api/hooks/use-get-footer'
import SearchModal from '../search/partials/search-modal'
import {useWishlistUtilities} from '../../context/wishlist'
import HeaderAccount from '../header/header-account'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import LoadingSpinner from '../loading-spinner'
import {Link as RouterLink} from 'react-router-dom'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {BRAND_LABEL} from '../../brand'
import useCorporateLayout from '../../hooks/use-corporate-layout'

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {bool} props.isOpen bool to check if drawer is open
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const KenwoodHeader = ({
    children,
    isOpen = false,
    onMenuClick = noop,
    onLogoClick = noop,
    onMyCartClick = noop,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    onMyAccountClick = noop,
    onWishlistClick = noop,
    ...props
}) => {
    const intl = useIntl()

    const {data} = useGetMainMenu('kenwood_')
    const [showLoading, setShowLoading] = useState(false)
    // tracking if users enter the popover Content,
    // so we can decide whether to close the menu when users leave account icons
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const hideAccountPref = useBooleanCustomPreference('hideAccount')
    const hideAccount = hideAccountPref !== null ? hideAccountPref : false
    const styles = useMultiStyleConfig('Header', {variant: 'kenwood'})
    const {data: wishListData} = useWishList()
    const wishListNumber = wishListData?.customerProductListItems?.length
    const headerLinks = data?.headerlinks ? data?.headerlinks?.[0]?.links : []
    const {icon: wishlistIconRef} = useWishlistUtilities()
    const {locale} = useMultiSite()
    const {data: footerData} = useGetFooterByContentType()
    const {showCorporateLayout} = useCorporateLayout()
    const accessibilityStatementLink = footerData?.footer_content?.footer_reference_components
        ?.find((item) => item?._content_type_uid === 'footerbottom')
        .footer_bottom_group?.copyright_group?.copyright_links?.find((item) =>
            /accessibility/i.test(item.link?.href),
        )

    return (
        <Box {...styles.headerBar} id="main-header">
            <Box {...styles.container} sx={styles.containerSpacing} {...props}>
                <Box {...styles.content}>
                    <Flex {...styles.iconsFlex}>
                        <Box sx={styles.hideOnDesktop}>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu',
                                    defaultMessage: 'Menu',
                                })}
                                title={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu.open_dialog',
                                    defaultMessage: 'Opens a dialog',
                                })}
                                icon={
                                    isOpen ? (
                                        <CloseIcon {...styles.burgerIcon} />
                                    ) : (
                                        <BurgerIcon {...styles.burgerIcon} />
                                    )
                                }
                                variant="unstyled"
                                onClick={onMenuClick}
                                {...styles.buttonIcons}
                            />
                        </Box>

                        <Box {...styles.bodyContainer} sx={styles.bodyContainerGap}>
                            <IconButton
                                aria-label={`${BRAND_LABEL} ${intl.formatMessage({
                                    id: 'header.button.assistive_msg.logo',
                                    defaultMessage: 'Logo',
                                })}`}
                                icon={<BrandLogo sx={styles.logo} />}
                                variant="unstyled"
                                role="link"
                                tabIndex={0}
                                onClick={showCorporateLayout ? noop : onLogoClick}
                                _focusVisible={{boxShadow: 'outline'}}
                            />
                            {accessibilityStatementLink?.link && (
                                <ChakraLink
                                    as={RouterLink}
                                    to={`/${locale.alias}${accessibilityStatementLink?.link.href}`}
                                    {...styles.srOnly}
                                >
                                    {accessibilityStatementLink?.link.title}
                                </ChakraLink>
                            )}
                            <Box sx={styles.hideOnMobile}>{children}</Box>
                        </Box>
                    </Flex>

                    {showLoading && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}

                    <Flex {...styles.linksAndIcons}>
                        <Flex {...styles.iconsFlex} sx={styles.iconsFlexGap}>
                            <SearchModal />

                            {!showCorporateLayout && !headerLinks?.[0] && (
                                <Link
                                    as={RouterLink}
                                    to={headerLinks?.[0]?.link?.href || '/'}
                                    key={headerLinks?.[0]?._metadata.uid}
                                    sx={styles.hideOnMobile}
                                >
                                    <SupportIcon {...styles.supportIcon} />
                                </Link>
                            )}

                            {!showCorporateLayout && !hideAccount && (
                                <IconButton
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Wishlist',
                                        id: 'header.button.assistive_msg.wishlist',
                                    })}
                                    icon={
                                        <>
                                            <FavouriteIcon sx={styles.wishlistIcon} />
                                            {wishListNumber > 0 && (
                                                <Badge
                                                    {...styles.wishlistBadge}
                                                    data-testid="cart-item-count"
                                                    variant="notification"
                                                >
                                                    {wishListNumber}
                                                </Badge>
                                            )}
                                        </>
                                    }
                                    variant="unstyled"
                                    onClick={onWishlistClick}
                                    {...styles.buttonIcons}
                                    sx={styles.hideOnMobile}
                                />
                            )}

                            {!hideAccount && (
                                <HeaderAccount
                                    setShowLoading={setShowLoading}
                                    headerLinks={headerLinks}
                                />
                            )}

                            {!notShoppableCountry && (
                                <Box position="relative">
                                    <MiniCartPopover onMyCartClick={onMyCartClick} />
                                    <Box
                                        ref={wishlistIconRef}
                                        top="190%"
                                        right="0%"
                                        position="absolute"
                                    ></Box>
                                </Box>
                            )}
                        </Flex>
                    </Flex>
                </Box>
            </Box>
        </Box>
    )
}

KenwoodHeader.propTypes = {
    children: PropTypes.node,
    isOpen: PropTypes.bool,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onWishlistClick: PropTypes.func,
    onMyCartClick: PropTypes.func,
    onStoreLocatorClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType}),
    ]),
}

export default KenwoodHeader
